/**
 * Delete confirmation dialog for shift series
 * Provides options to delete all shifts or only future shifts
 */

import { useLingui } from '@lingui/react';
import { formatDateDisplay } from '@/lib/format';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useDeleteShiftSeries } from '@/hooks/useShifts';

export interface DeleteSeriesDialogProps {
  patternId: string;
  shiftCount: number;
  /** The date of the selected shift (YYYY-MM-DD), used to delete future shifts starting from this date (inclusive) */
  selectedShiftDate?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function DeleteSeriesDialog({
  patternId,
  shiftCount,
  selectedShiftDate,
  open,
  onOpenChange,
  onSuccess,
}: DeleteSeriesDialogProps) {
  const { i18n } = useLingui();
  const deleteShiftSeries = useDeleteShiftSeries();

  // Handle delete all action
  const handleDeleteAll = async () => {
    try {
      await deleteShiftSeries.mutateAsync({
        patternId,
        futureOnly: false,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch {
      // Keep dialog open on error so user can see error toast and retry
    }
  };

  // Handle delete future only action
  // Deletes shifts starting from the selected shift date (inclusive)
  const handleDeleteFuture = async () => {
    try {
      await deleteShiftSeries.mutateAsync({
        patternId,
        futureOnly: true,
        fromDate: selectedShiftDate,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch {
      // Keep dialog open on error so user can see error toast and retry
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {i18n._({
              id: 'shifts.series.delete.title',
              message: 'Delete Series',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {i18n._({
                  id: 'shifts.series.shiftCount',
                  message: '{count} shifts in this series',
                  values: { count: shiftCount },
                })}
              </p>

              {/* Warning message */}
              <div className="flex items-start gap-2 rounded-md border border-warning-on-surface/40 bg-warning-muted p-3 text-warning-on-surface">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span className="text-sm" data-testid="warning-text">
                  {i18n._({
                    id: 'shifts.series.delete.warning',
                    message: 'This action cannot be undone',
                  })}
                </span>
              </div>
              {/* #2119 UX pass: what each choice removes and who hears about it */}
              <p className="text-sm" data-testid="delete-series-consequence">
                {selectedShiftDate
                  ? i18n._({
                      id: 'shifts.series.delete.consequenceFrom',
                      message:
                        '"Delete future shifts only" removes the shifts from {date} on. Published shifts disappear from the employee\'s app, and the employee is notified.',
                      values: { date: formatDateDisplay(selectedShiftDate, i18n.locale) },
                    })
                  : i18n._({
                      id: 'shifts.series.delete.consequence',
                      message:
                        'Published shifts disappear from the employee\'s app, and the employee is notified.',
                    })}
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            variant="outline"
            className="h-auto min-h-10 w-full whitespace-normal"
            onClick={handleDeleteFuture}
            disabled={deleteShiftSeries.isPending}
            data-testid="delete-future-button"
          >
            {deleteShiftSeries.isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {i18n._({
              id: 'shifts.series.delete.future',
              message: 'Delete future shifts only',
            })}
          </Button>
          <Button
            variant="destructive"
            className="h-auto min-h-10 w-full whitespace-normal"
            onClick={handleDeleteAll}
            disabled={deleteShiftSeries.isPending}
            data-testid="delete-all-button"
          >
            {deleteShiftSeries.isPending && (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {i18n._({
              id: 'shifts.series.delete.all',
              message: 'Delete all shifts',
            })}
          </Button>
          {/* #2119: stacked full-width buttons, Cancel last; the German labels did not fit in one row. */}
          <AlertDialogCancel className="mt-0 w-full" data-testid="cancel-button">
            {i18n._({ id: 'common.cancel', message: 'Cancel' })}
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
